/**
 * Reading metric results into what the cards and charts take.
 */
import type { MetricResult } from '../../../../common/hooks/use-analytics-api.hook';
import { AnalyticsPeriod, Granularity, bucketKeys, fillSeries, trendPoints } from '../../../../components/design-system/analytics/period';
import type { TrendPoint } from '../../../../components/design-system/analytics';
import type { Pair } from './viz-reading';

export const valueOf = (m: MetricResult | null | undefined): number | null => (m && typeof m.value === 'number' ? m.value : null);

export const pairOf = (m: MetricResult | null | undefined): Pair => ({ value: valueOf(m), previous: m?.previous ?? null });

/** A KpiCard delta, only when both periods have a figure. */
export function deltaOf(m: MetricResult | null | undefined): { current: number; previous: number } | undefined {
  const v = valueOf(m);
  return v != null && typeof m?.previous === 'number' ? { current: v, previous: m.previous } : undefined;
}

/** The current series for a sparkline, gaps filled with zero. */
export function sparkOf(m: MetricResult | null | undefined, period: AnalyticsPeriod, granularity: Granularity): number[] {
  return fillSeries(m?.breakdown, bucketKeys(period.startDate, period.endDate, granularity));
}

/** Current and previous series as TrendChart points. */
export function trendOf(m: MetricResult | null | undefined, period: AnalyticsPeriod, granularity: Granularity, locale: string): TrendPoint[] {
  return trendPoints(m?.breakdown, m?.previousBreakdown, period, granularity, locale);
}

/** A breakdown as a key → value map (products → leads). */
export function breakdownMap(m: MetricResult | null | undefined): Map<string, number> {
  return new Map((m?.breakdown ?? []).map((r) => [r.key, r.value]));
}

/** Whether a period and the one before it saw nobody at all — a vendor still to go live. */
export function noTraffic(visitors: MetricResult | null | undefined): boolean {
  return !valueOf(visitors) && !visitors?.previous;
}
